import { BenchmarkingTable } from '@/components/common/Table/BenchmarkingTable/BenchmarkingTable'
import { getEntityDetailsMap } from '@/components/data/Benchmarking/utils/getEntityDetails'
import { getProcessedData } from '@/components/data/Benchmarking/utils/getProcessedData'
import { getTopEntityIds } from '@/components/data/Benchmarking/utils/getTopEntityIds'
import { normalizeTableData } from '@/components/data/Benchmarking/utils/normalizeTableData'
import { BenchmarkingItemWithUnit, BenchmarkingReportSettings } from '@/hooks'
import { Timeline } from '@/utils/date'
import { formatDateRangeForTable } from '@/utils/formatDateForTable'
import { SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useColumns } from './useColumns'

interface BenchmarkingTableContainerProps {
    data: BenchmarkingItemWithUnit[]
    reportSettings: BenchmarkingReportSettings
    currentTimeline: Timeline
    baselineYearTimeline?: Timeline
    previousYearTimeline?: Timeline
}

export const BenchmarkingTableContainer = ({
    data,
    reportSettings,
    currentTimeline,
    baselineYearTimeline,
    previousYearTimeline,
}: BenchmarkingTableContainerProps) => {
    const baselineYearLabel = baselineYearTimeline
        ? formatDateRangeForTable(
              baselineYearTimeline.startDate,
              baselineYearTimeline.endDate
          )
        : ''
    const previousYearLabel = previousYearTimeline
        ? formatDateRangeForTable(
              previousYearTimeline.startDate,
              previousYearTimeline.endDate
          )
        : ''

    const currentYearLabel = formatDateRangeForTable(
        currentTimeline.startDate,
        currentTimeline.endDate
    )

    const labels = useMemo(
        () => ({
            currentYear: currentYearLabel,
            baselineYear: baselineYearLabel,
            previousYear: previousYearLabel,
        }),
        [currentYearLabel, baselineYearLabel, previousYearLabel]
    )

    const processedData = useMemo(
        () => getProcessedData(data, reportSettings, labels),
        [data, reportSettings, labels]
    )

    const [sorting, setSorting] = useState<SortingState>([])

    const allEntityIds = useMemo(() => getTopEntityIds(data, -1), [data])
    const entities = useMemo(
        () => getEntityDetailsMap(data, allEntityIds),
        [data, allEntityIds]
    )
    const squareFootages = useMemo(
        () =>
            Array.from(entities.values()).reduce(
                (acc, entity) => ({
                    ...acc,
                    [entity.id]: entity.squareFootage,
                }),
                {}
            ),
        [entities]
    )

    const columns = useColumns(
        data,
        entities,
        squareFootages,
        reportSettings,
        labels
    )

    const normalizedData = useMemo(
        () => normalizeTableData(processedData, entities, reportSettings),
        [processedData, entities, reportSettings]
    )

    return (
        <BenchmarkingTable
            data={normalizedData}
            columns={columns}
            sorting={sorting}
            setSorting={setSorting}
        />
    )
}
